import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { http, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, CheckCircle, ArrowLeft } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing or invalid reset link."); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await http.post("/auth/password/reset", { token, new_password: password });
      setDone(true);
      toast.success("Password updated.");
      setTimeout(() => nav("/login"), 1400);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f8fafc]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhZnJpY2FuJTIwcmVhbCUyMGVzdGF0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODU1Mzk4NDJ8MA&ixlib=rb-4.1.0&q=85" alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0a1128]/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><span className="text-[#00b4d8] font-black text-2xl font-display">M</span></span>
            <span className="font-display font-black text-2xl">MAYOTO</span>
          </Link>
          <div>
            <p className="text-[#d4af37] font-display font-bold tracking-[0.25em] text-xs">CHOOSE A NEW PASSWORD</p>
            <h1 className="mt-3 font-display font-black text-5xl leading-tight max-w-md">One more step and you're back in.</h1>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#00b4d8] mb-6"><ArrowLeft size={16} /> Back to login</Link>
          <h2 className="font-display font-black text-3xl text-[#0a1128]" data-testid="reset-title">Set a new password</h2>
          <p className="mt-2 text-slate-600">Choose a strong password — minimum 8 characters.</p>

          {done ? (
            <div className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-200 p-6" data-testid="reset-done">
              <div className="flex items-center gap-3"><CheckCircle size={24} className="text-emerald-600" weight="fill" /><p className="font-display font-bold text-emerald-900">Password updated</p></div>
              <p className="mt-2 text-sm text-emerald-900">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
              <div>
                <Label>New Password</Label>
                <div className="relative">
                  <Input type={show ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)} data-testid="reset-password" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-slate-500">Minimum 8 characters.</p>
              </div>
              <div>
                <Label>Confirm Password</Label>
                <Input type={show ? "text" : "password"} required value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="reset-confirm" />
              </div>
              <Button type="submit" disabled={loading || !token} className="btn-shine w-full bg-[#00b4d8] hover:bg-[#0096c7] rounded-full py-6 text-base" data-testid="reset-submit">
                {loading ? "Updating…" : "Update password"}
              </Button>
              {!token && <p className="text-sm text-red-600">Reset link missing. Request a new one from the <Link to="/forgot-password" className="underline">forgot password page</Link>.</p>}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
